import { wrapCreateBrowserRouter } from "@sentry/react"
import { createBrowserRouter } from "react-router-dom"

import { RootLayout } from "@/layouts/RootLayout"
import { StartPage } from "@/pages/StartPage"
import { ErrorBoundary, SessionLayout } from "./layouts/SessionLayout"
import {
  SeveranceErrorBoundary,
  SeveranceSessionLayout,
} from "./layouts/SeveranceSessionLayout"
import { CardIdVerifyPage } from "./pages/CardIdVerifyPage"
import { EmailVerifyPage } from "./pages/EmailVerifyPage"
import { ErrorPage } from "./pages/ErrorPage"
import { HomePage } from "./pages/HomePage"
import { InvitationError } from "./pages/InvitationError"
import { NotFoundPage } from "./pages/NotFoundPage"
import { ReservationOfRightsPage } from "./pages/ReservationOfRightsPage"
import { SuccessPage } from "./pages/SuccessPage"
import { SeveranceStartPage } from "./pages/severance/SeveranceStartPage"
import { VideoPage } from "./pages/VideoPage"

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter)

const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "",
        element: <HomePage />,
      },
      {
        path: "success",
        element: <SuccessPage />,
      },
      {
        path: "invitation/error",
        element: <InvitationError />,
      },
      {
        path: "validations/:token",
        lazy: async () => {
          const { ValidationPage } = await import("@/pages/ValidationPage")
          return { Component: ValidationPage }
        },
      },
      {
        path: "severance/:token_id",
        element: <SeveranceSessionLayout />,
        ErrorBoundary: SeveranceErrorBoundary,
        children: [
          {
            path: "",
            index: true,
            element: <SeveranceStartPage />,
          },
          {
            path: "documents",
            lazy: async () => {
              const { SeveranceDocumentsAndRightsPage } = await import(
                "@/pages/severance/SeveranceDocumentsAndRightsPage"
              )
              return { Component: SeveranceDocumentsAndRightsPage }
            },
          },
          {
            path: "bank",
            lazy: async () => {
              const { SeveranceBankAccountPage } = await import(
                "@/pages/severance/SeveranceBankAccountPage"
              )
              return { Component: SeveranceBankAccountPage }
            },
          },
          {
            path: "status",
            lazy: async () => {
              const { SeveranceStatusPage } = await import(
                "@/pages/severance/SeveranceStatusPage"
              )
              return { Component: SeveranceStatusPage }
            },
          },
        ],
      },
      {
        path: ":token_id",
        element: <SessionLayout />,
        ErrorBoundary: ErrorBoundary,
        children: [
          {
            path: "",
            index: true,
            element: <StartPage />,
          },
          {
            path: "email",
            element: <EmailVerifyPage />,
          },
          {
            path: "card",
            element: <CardIdVerifyPage />,
          },
          {
            path: "video",
            element: <VideoPage />,
          },
          {
            path: "attachments",
            lazy: async () => {
              const { AttachmentsPage } = await import(
                "@/pages/AttachmentsPage"
              )
              return { Component: AttachmentsPage }
            },
          },
          {
            path: "rights",
            element: <ReservationOfRightsPage />,
          },
          {
            path: "bank",
            lazy: async () => {
              const { BankAccountsPage } = await import(
                "@/pages/BankAccountsPage"
              )
              return { Component: BankAccountsPage }
            },
          },
          {
            path: "payment",
            lazy: async () => {
              const { PaymentPage } = await import("@/pages/PaymentPage")
              return { Component: PaymentPage }
            },
          },
          {
            path: "signature",
            lazy: async () => {
              const { SignaturePage } = await import("@/pages/SignaturePage")
              return { Component: SignaturePage }
            },
          },
          {
            path: "status",
            lazy: async () => {
              const { StatusPage } = await import("@/pages/StatusPage")
              return { Component: StatusPage }
            },
          },
        ],
      },
      {
        path: "*",
        element: <NotFoundPage />,
      },
    ],
  },
])

export default router
